import type { TranslationTree } from '../../locale.model';

export const pagesEn: TranslationTree = {
  manufacturers: {
    title: 'Manufacturers',
    subtitle: 'Manage manufacturers available for licensing.',
    newButton: 'New Manufacturer',
    summaryAria: 'Manufacturers summary',
    registered: 'Registered',
    searchPlaceholder: 'Search manufacturers...',
    loading: 'Loading manufacturers...',
    empty: 'No manufacturers found.',
    table: {
      manufacturer: 'Manufacturer',
      description: 'Description',
      createdAt: 'Created at',
      actions: 'Actions',
    },
    editAria: 'Edit manufacturer',
    deleteAria: 'Delete manufacturer',
    delete: {
      title: 'Confirm deletion',
      message: 'Do you really want to remove manufacturer {{name}}?',
      cancel: 'Cancel',
      confirm: 'Delete',
      deleting: 'Deleting...',
    },
    form: {
      editTitle: 'Edit manufacturer',
      createTitle: 'New manufacturer',
      subtitle: 'Fill in the details to register or update the manufacturer.',
      name: 'Name',
      description: 'Description',
      cancel: 'Cancel',
      saving: 'Saving...',
      saveChanges: 'Save changes',
      create: 'Create manufacturer',
    },
  },
  resellers: {
    title: 'Reseller Management',
    subtitle: 'Register and manage your partner resellers.',
    summaryAria: 'Resellers summary',
    stats: {
      total: 'Total Resellers',
      active: 'Active Resellers',
      inactive: 'Inactive Resellers',
      filtered: 'Filtered',
    },
    searchPlaceholder: 'Search by code, name, email or region...',
    statusFilter: {
      all: 'All',
      active: 'Active',
      inactive: 'Inactive',
    },
    commissionReport: 'Commission Report',
    newButton: 'New Reseller',
    loading: 'Loading resellers...',
    empty: 'No resellers found.',
    table: {
      code: 'Code',
      name: 'Name / Legal Name',
      contact: 'Contact',
      region: 'Region',
      clients: 'Clients',
      status: 'Status',
      inactiveAt: 'Inactive date',
      actions: 'Actions',
    },
    clientsCount: '{{count}} client(s)',
    statusActive: 'Active',
    statusInactive: 'Inactive',
    moreActionsAria: 'More actions',
    edit: 'Edit',
    remove: 'Remove',
    report: {
      title: 'Commission Report',
      subtitle: 'Commission report per reseller based on linked clients.',
      period: 'Period: Current Month',
      taxRate: 'Rate (%):',
      baseTotal: 'Total Base',
      commissionTotal: 'Total Commissions',
      averagePerReseller: 'Average per Reseller',
      table: {
        code: 'Code',
        reseller: 'Reseller',
        clients: 'Clients',
        baseValue: 'Base Value',
        tax: 'Rate',
        commission: 'Commission',
        status: 'Status',
      },
      noData: 'No commission data available.',
      footer: '{{resellers}} reseller(s) with {{clients}} client(s) in total.',
      close: 'Close',
      exportPdf: 'Export PDF',
      statusPending: 'Pending',
    },
    form: {
      editTitle: 'Edit Reseller',
      createTitle: 'New Reseller',
      subtitle: 'Fill in the reseller information.',
      code: 'Code',
      status: 'Status',
      name: 'Name / Legal Name',
      email: 'Email',
      phone: 'Phone',
      region: 'Region',
      cancel: 'Cancel',
      saving: 'Saving...',
      saveChanges: 'Save changes',
      create: 'Register Reseller',
      errors: {
        phoneInvalid: 'Enter a valid phone number or clear the field.',
        required: 'Fill in code, status and name / legal name.',
      },
    },
    clientsModal: {
      title: 'Clients of {{name}}',
      subtitle: 'Code: {{code}} - {{count}} linked client(s)',
      searchPlaceholder: 'Search by company, tax ID or name...',
      linked: 'Linked ({{count}})',
      available: 'Available ({{count}})',
      loading: 'Loading linked clients...',
      noLinked: 'No linked clients',
      allLinked: 'All clients are already linked',
      linking: 'Linking...',
      link: 'Link',
      close: 'Close',
    },
    delete: {
      title: 'Confirm deletion',
      message: 'Do you really want to remove reseller {{name}}?',
      cancel: 'Cancel',
      confirm: 'Delete',
      deleting: 'Deleting...',
    },
  },
  menus: {
    title: 'Menus',
    subtitle: 'Manage system menus and submenus.',
    addMenu: 'Add menu',
    summaryAria: 'Menus summary',
    registered: 'Registered menus',
    submenus: 'Submenus',
    searchPlaceholder: 'Search menus and submenus...',
    expandAll: 'Expand all',
    collapseAll: 'Collapse all',
    loading: 'Loading menus...',
    emptySearch: 'No menus found for this search.',
    empty: 'No menus registered.',
    reorderHint:
      'Use ↑↓ or drag by ⋮⋮ to reorder. The number on the left reflects the order saved in the database. Menus can only be moved within the same section.',
    clearSearchToReorder: 'Clear the search to reorder menus and submenus.',
    sectionInfo: 'Section: {{section}} (order_section: {{orderSection}}) · {{count}} submenu(s)',
    sectionGroupAria: 'Section {{section}}',
    sectionGroupMeta: '{{count}} menu(s) · order_section: {{orderSection}}',
    sectionGroupBadge: 'Section',
    expandSectionAria: 'Expand or collapse section {{section}}',
    menuSubmenuCount: '{{count}} submenu(s)',
    moveUpMenu: 'Move menu up',
    moveDownMenu: 'Move menu down',
    expandSubmenusAria: 'Expand or collapse submenus of {{title}}',
    addSubmenu: '+ Submenu',
    editMenuAria: 'Edit menu',
    deleteMenuAria: 'Delete menu',
    dragMenuAria: 'Drag menu',
    dragMenuTitle: 'Drag to reorder menu',
    noSubmenus: 'No submenus registered.',
    marker: 'Marker: {{bullet}}',
    moveUpSubmenu: 'Move submenu up',
    moveDownSubmenu: 'Move submenu down',
    editSubmenuAria: 'Edit submenu',
    deleteSubmenuAria: 'Delete submenu',
    dragSubmenuAria: 'Drag submenu',
    dragSubmenuTitle: 'Drag to reorder submenu',
    deleteMenu: {
      title: 'Delete menu',
      message: 'Are you sure you want to delete {{title}}? All submenus will be removed.',
      cancel: 'Cancel',
      confirm: 'Delete',
      deleting: 'Deleting...',
    },
    deleteSubmenu: {
      title: 'Delete submenu',
      message: 'Are you sure you want to delete {{title}}? This action cannot be undone.',
      cancel: 'Cancel',
      confirm: 'Delete',
      deleting: 'Deleting...',
    },
    menuForm: {
      editTitle: 'Edit menu',
      createTitle: 'Add menu',
      subtitle: 'Fill in the main menu details.',
      title: 'Title',
      path: 'Path',
      section: 'Section',
      svg: 'SVG',
      saxIcon: 'Sax Icon',
      orderSection: 'Section order',
      orderHint: 'Menu order in the list is set by dragging on the main screen.',
      cancel: 'Cancel',
      saving: 'Saving...',
      saveChanges: 'Save changes',
      create: 'Create menu',
    },
    submenuForm: {
      editTitle: 'Edit submenu',
      createTitle: 'Add submenu',
      subtitle: 'Fill in the submenu details.',
      title: 'Title',
      path: 'Path',
      marker: 'Marker',
      saxIcon: 'Sax Icon',
      cancel: 'Cancel',
      saving: 'Saving...',
      saveChanges: 'Save changes',
      create: 'Create submenu',
    },
    bullet: {
      bullet: 'Bullet (dot)',
      line: 'Line',
    },
  },
  ambience: {
    title: 'Ambience',
    subtitle: 'Manage ambience items and their types for the system.',
    tabs: {
      items: 'Ambience items',
      types: 'Item types',
    },
    addItem: 'Add Item',
    addType: 'Add item type',
    loadingItems: 'Loading items...',
    emptyItems: 'No ambience items found.',
    loadingTypes: 'Loading types...',
    emptyTypes: 'No item types found.',
    table: {
      icon: 'Icon',
      name: 'Name',
      type: 'Type',
      description: 'Description',
      actions: 'Actions',
    },
    editItemAria: 'Edit item',
    deleteItemAria: 'Delete item',
    editTypeAria: 'Edit type',
    deleteTypeAria: 'Delete type',
    typeForm: {
      editTitle: 'Edit item type',
      createTitle: 'Add item type',
      editHint: 'Update the ambience item type details.',
      createHint: 'Add a new ambience item type for your system.',
      name: 'Name',
      description: 'Description',
      saving: 'Saving...',
      save: 'Save',
      cancel: 'Cancel',
    },
    itemForm: {
      editTitle: 'Edit item',
      createTitle: 'Add item',
      editHint:
        'Update the ambience item details. The icon will only be replaced if a new file is uploaded.',
      createHint: 'Add a new ambience item for your system.',
      iconPreviewAlt: 'Icon preview',
      currentIconAlt: 'Current icon',
      name: 'Name',
      itemType: 'Item type',
      noTypeAvailable: 'No type available',
      icon: 'Icon',
      iconOptional: ' (optional)',
      keepCurrentIcon: 'Keep current icon',
      noFile: 'No file selected',
      browse: 'Browse',
      saving: 'Saving...',
      save: 'Save',
      cancel: 'Cancel',
    },
    deleteType: {
      title: 'Confirm deletion',
      message: 'Do you really want to delete type {{name}}?',
      cancel: 'Cancel',
      confirm: 'Delete',
      deleting: 'Deleting...',
    },
    deleteItem: {
      title: 'Confirm deletion',
      message: 'Do you really want to delete item {{name}}?',
      cancel: 'Cancel',
      confirm: 'Delete',
      deleting: 'Deleting...',
    },
    feedback: {
      typeUpdated: 'Type updated',
      typeUpdatedMessage: 'Type "{{name}}" was updated successfully.',
      typeCreated: 'Type created',
      typeCreatedMessage: 'Type "{{name}}" was registered successfully.',
      typeDeleted: 'Type deleted',
      typeDeletedMessage: 'Type "{{name}}" was deleted successfully.',
      typeErrorCreate: 'Error creating type',
      typeErrorUpdate: 'Error updating type',
      typeErrorDelete: 'Error deleting type',
      itemUpdated: 'Item updated',
      itemUpdatedMessage: 'Item "{{name}}" was updated successfully.',
      itemCreated: 'Item created',
      itemCreatedMessage: 'Item "{{name}}" was registered successfully.',
      itemDeleted: 'Item deleted',
      itemDeletedMessage: 'Item "{{name}}" was deleted successfully.',
      itemErrorCreate: 'Error creating item',
      itemErrorUpdate: 'Error updating item',
      itemErrorDelete: 'Error deleting item',
      ok: 'OK',
    },
  },
  palettes: {
    list: {
      title: 'Color schemes',
      newButton: 'New color scheme',
      loading: 'Loading palettes...',
      empty: 'No color palettes found.',
      table: {
        name: 'Name',
        action: 'Action',
      },
      editAria: 'Edit palette',
      deleteAria: 'Delete palette',
      delete: {
        title: 'Confirm deletion',
        message: 'Do you really want to delete palette {{name}}?',
        cancel: 'Cancel',
        confirm: 'Delete',
        deleting: 'Deleting...',
      },
      feedback: {
        deleted: 'Palette deleted',
        deletedMessage: 'Palette "{{name}}" was deleted successfully.',
        deleteError: 'Error deleting palette',
        ok: 'OK',
      },
    },
    form: {
      editTitle: 'Edit color scheme',
      createTitle: 'New color scheme',
      loading: 'Loading palette...',
      nameLabel: 'Scheme name',
      sections: {
        cardColors: 'Card colors',
        loginScreen: 'Login screen',
        securityDashboard: 'Security dashboard',
        dashboard: 'Dashboard',
        verticalMenu: 'Vertical menu',
        horizontalMenu: 'Horizontal menu',
        priorities: 'Priorities',
      },
      light: 'Light',
      dark: 'Dark',
      card: 'Card',
      colorN: 'Color {{index}}',
      priorityLevel: 'priority.level_{{level}}',
      cancel: 'Cancel',
      saving: 'Saving...',
      save: 'Save',
      create: 'Create',
      validation: 'Validation',
      nameRequired: 'Enter the scheme name.',
      notFound: 'Palette not found.',
      backToList: 'Back to color schemes',
      feedback: {
        updated: 'Palette updated',
        created: 'Palette created',
        successMessage: 'Palette "{{name}}" was {{action}} successfully.',
        errorCreate: 'Error creating palette',
        errorUpdate: 'Error updating palette',
        ok: 'OK',
      },
      fields: {
        background: 'Background color',
        darkBackground: 'Dark background color',
        accent: 'Accent color',
        text: 'Text color',
        contrast: 'Contrast color',
        iconAccent: 'Icon accent color',
        primary1: 'Primary color 1',
        primary2: 'Primary color 2',
        primary3: 'Primary color 3',
        primaryBlack1: 'Primary black 1',
        primaryBlack2: 'Primary black 2',
        primaryBlack3: 'Primary black 3',
        primaryLight1: 'Primary light 1',
        primaryLight2: 'Primary light 2',
        primaryLight3: 'Primary light 3',
        secondary1: 'Secondary color 1',
        secondary2: 'Secondary color 2',
        secondary3: 'Secondary color 3',
        secondary4: 'Secondary color 4',
        secondary5: 'Secondary color 5',
        secondary6: 'Secondary color 6',
        secondary7: 'Secondary color 7',
        secondary8: 'Secondary color 8',
        secondary9: 'Secondary color 9',
        secondary10: 'Secondary color 10',
      },
    },
  },
  clients: {
    title: 'Companies',
    subtitle: 'Create, edit or remove companies, with vertical and manufacturer links.',
    newButton: 'New Company',
    summaryAria: 'Companies summary',
    registered: 'Registered',
    searchPlaceholder: 'Search companies...',
    filterInstallation: 'Installation',
    filterStatus: 'Status',
    filterAllInstallation: 'All',
    filterAllStatus: 'All',
    searchHint: 'Text search filters only the results on the current page.',
    loading: 'Loading companies…',
    refreshing: 'Updating list…',
    empty: 'No companies found.',
    table: {
      company: 'Company',
      vertical: 'Vertical',
      installation: 'Installation',
      createdAt: 'Created at',
      status: 'Status',
      actions: 'Actions',
    },
    licensingAria: 'Licensing overview',
    licensingTitle: 'Licensing',
    editAria: 'Edit company',
    profilesAria: 'Manage profiles',
    profilesTitle: 'Manage profiles',
    usersAria: 'Manage users',
    usersTitle: 'Manage users',
    integrationsAria: 'Integrations',
    integrationsTitle: 'Integrations',
    deleteAria: 'Delete company',
    delete: {
      title: 'Confirm deletion',
      message: 'Do you really want to delete company {{name}}?',
      deleting: 'Deleting company…',
      cancel: 'Cancel',
      confirm: 'Delete',
    },
    success: {
      created: 'Company created',
      createdMessage: '{{name}} was registered successfully. The list was updated.',
      deleted: 'Company deleted',
      deletedMessage: 'Company {{name}} was deleted successfully. The list was updated.',
      updated: 'Company updated',
      updatedMessage: 'Changes to {{name}} were saved successfully. The list was updated.',
      ok: 'OK',
    },
    editModal: {
      savingCreate: 'Creating company…',
      savingEdit: 'Saving changes…',
      sectionsAria: 'Company sections',
      tabs: {
        info: 'General information',
        config: 'Settings',
        logos: 'Logos',
        ambientacao: 'Ambience',
        analiticos: 'Analytics',
        modulos: 'Modules',
        licenca: 'License',
      },
      vertical: 'Vertical',
      name: 'Name',
      legalName: 'Legal name',
      document: 'Document',
      status: 'Status',
      installationType: 'Installation type',
      subdomain: 'Subdomain',
      subdomainLocked: 'Subdomain cannot be changed.',
      subdomainLockedTitle: 'Subdomain cannot be changed after company creation',
      manufacturers: 'Manufacturers enabled for the company',
      noManufacturers: 'No manufacturers loaded.',
      logo: 'Logo',
      noPreview: 'No preview',
      logoUploadHint:
        'Logo upload is done in a separate step to /company/:id/logo after saving the company.',
      select: 'Select',
      active: 'Active',
      inactive: 'Inactive',
      legacyV1: 'v1 is legacy mode.',
      cancel: 'Cancel',
      saving: 'Saving…',
      saveChanges: 'Save changes',
      create: 'Create company',
      cancelConfirmTitle: 'Discard changes?',
      errors: {
        waitSettings: 'Wait for settings to finish loading.',
        settingsNotLoaded:
          'Settings have not loaded yet. Open the Settings tab and wait for loading to finish.',
        requiredCreate: 'Fill in vertical, name, legal name, document and subdomain.',
        requiredEdit: 'Fill in vertical, name, legal name and document.',
        noStatus: 'No status available. Check the status endpoint or administrator permissions.',
        invalidInstallation: 'Select a valid installation type (Cloud, On-premise or Hybrid).',
        moduleVersion: 'Select the module management version (v1 or v2).',
        logoUploadFailed: 'Company was saved, but logo upload failed: {{error}}',
        selectStatus: 'Select the company status.',
      },
      createTitle: 'New company registration',
      editTitle: 'Edit company',
      subtitle: 'Check if the company already exists before proceeding.',
      savingWait: 'Please wait, we are communicating with the server.',
      noStatusLoaded: 'No status loaded. GET /v1/licensing/companies/statuses (admin) is required.',
      modulesVersion: 'Module management version',
      legacyV1Hint:
        'If using this mode, all profile management must be done via the Fisitalview master module.',
      logoPreviewAlt: 'Logo preview',
      loadingSettings: 'Loading settings…',
      sectionComingSoon: 'Content for this section will be added soon.',
      cancelEditTitle: 'Cancel editing',
      cancelEditMessage: 'Are you sure you want to cancel? Unsaved changes will be discarded.',
      continueEditing: 'Continue editing',
      confirmCancel: 'Yes, cancel',
      tabsExtra: {
        cores: 'Custom colors',
        idioma: 'Language',
        localVisit: 'Local Visit',
        llms: 'LLMs',
      },
    },
    backToCompanies: 'Back to companies',
    companyFallback: 'Company',
    editTitle: 'Edit',
    errors: {
      companyNotIdentified: 'Company not identified.',
      operationFailed: 'Could not complete the operation.',
      sessionExpired: 'Invalid or expired session.',
    },
    workspace: {
      underConstruction: 'Area under construction for company',
    },
    routesAccess: {
      title: 'Modules',
      description:
        'Define which categories and modules this company can view in the SaaS. Checking a category enables all modules with a valid route; you can enable individual modules by unchecking specific items.',
      expandAll: 'Expand all',
      collapseAll: 'Collapse all',
      loading: 'Loading modules and categories…',
      empty: 'No categories or modules registered.',
      emptyCategory: 'No modules in this category.',
      incompleteRoute: 'Incomplete route in catalog',
      uncategorized: 'Uncategorized',
      errors: {
        waitLoading: 'Wait for modules to finish loading.',
        notLoaded: 'Modules have not loaded yet. Wait for loading to finish.',
      },
    },
    ambientPermissions: {
      title: 'Enabled ambience items',
      description:
        'Define which ambience items this company can use in the system. Checking a type enables all items in the category; you can enable individual items by unchecking specific ones.',
      loading: 'Loading ambience items…',
      empty: 'No ambience types or items registered.',
      emptyType: 'No items in this type.',
      errors: {
        waitLoading: 'Wait for ambience items to finish loading.',
        notLoaded: 'Ambience items have not loaded yet. Wait for loading to finish.',
      },
    },
    entitlements: {
      loading: 'Loading entitlements…',
      description:
        'Server-side licensing for cloud/hybrid clients. Changes apply immediately in fv-api cloud.',
      lastUpdated: 'Last updated:',
      quotasTitle: 'Quotas and validity',
      activeFrom: 'Active from (optional)',
      expiresAt: 'Expires at (optional)',
      locations: 'Locations',
      cameras: 'Cameras',
      users: 'Users',
      sensors: 'Sensors',
      storageGb: 'Storage (GB)',
      manufacturers: 'Manufacturers',
      saveSuccess: 'Entitlements saved successfully.',
      saveButton: 'Save entitlements',
      errors: {
        loadFailed: 'Could not load entitlements.',
        saveFailed: 'Failed to save entitlements. Check the data.',
      },
    },
    licenseTab: {
      loading: 'Loading license…',
      status: 'Status:',
      validity: 'Validity',
      quotas: 'Quotas',
      history: 'History',
      issuedAt: 'Issued at',
      expiresAt: 'Expires at',
      download: 'Download',
      noLicense: 'No license issued for this client.',
      generateTitle: 'Generate new license (.lic)',
      fingerprintHint:
        'Paste the fingerprint generated on the on-prem server (`GET /license/fingerprint` or CLI script).',
      fingerprint: 'Fingerprint (64 hex)',
      generating: 'Generating…',
      generateButton: 'Generate and download .lic',
      errors: {
        loadFailed: 'Could not load license data.',
        fingerprintRequired: 'Enter the on-prem machine fingerprint.',
        generateFailed: 'Failed to generate license. Check data and cloud configuration.',
        redownloadFailed: 'Could not download this license again.',
      },
    },
    licensingOverview: {
      loading: 'Loading data…',
      entitlements: 'Entitlements',
      status: 'Status:',
      noLicense: 'No license issued.',
      analytics: 'Analytics:',
      manufacturers: 'Manufacturers:',
      fingerprint: 'Fingerprint:',
      usageUsers: 'Registered users',
      usageLocations: 'Registered locations',
      usageCameras: 'Registered cameras',
      usageSensors: 'Registered sensors',
      usageStorage: 'Storage used',
      onPremiseHint:
        'Real usage (registrations vs limits) is only available for cloud/hybrid clients. For on-premise, usage is queried on the client local server.',
      errors: {
        loadFailed: 'Could not load licensing data.',
      },
    },
    profileEdit: {
      sectionsAria: 'Profile sections',
      tabs: { general: 'General', routesModules: 'Routes / Modules' },
      warning:
        'Changes to this profile affect access for all users linked to it. Profile routes must be a subset of routes enabled for the company.',
      warningUsers: 'all users',
      nameLabel: 'Profile name',
      createTitle: 'New profile',
      editTitle: 'Edit profile',
      defaultName: 'Profile',
      create: 'Create profile',
      errors: {
        nameRequired: 'Enter the profile name.',
        nameRequiredDetailsTab: 'Enter the profile name in the «Details» tab.',
      },
    },
    profileRoutes: {
      helpTitle: 'Where to configure',
      helpText:
        'Open Clients → profile icon (shield) → edit profile → «Routes / Modules» tab. Check categories/modules; use ↑↓ or drag by ⋮⋮ on the right; fill in «Section name» and «Menu icon».',
      title: 'Profile routes',
      subtitle: 'Subset of routes enabled for the company.',
      empty: 'No routes enabled for the company. Configure the Modules tab in Edit company first.',
      columns: {
        order: 'Order',
        orderHint: 'Use ↑↓ to reorder',
        category: 'Category',
        sectionName: 'Section name',
        actions: 'Actions',
        module: 'Module',
        menuIcon: 'Menu icon',
      },
      drag: {
        moveCategoryUp: 'Move category up',
        moveCategoryDown: 'Move category down',
        moveModuleUp: 'Move module up',
        moveModuleDown: 'Move module down',
        dragCategory: 'Drag category',
        dragModule: 'Drag module',
      },
      placeholders: {
        sectionName: 'Menu name',
        checkCategory: 'Check the category…',
        checkModule: 'Check the module…',
      },
      errors: {
        waitLoading: 'Wait for modules to finish loading.',
        profileNotIdentified: 'Profile not identified.',
      },
    },
    profilesPage: {
      title: 'Profiles',
      registered: '{{count}} registered',
      searchName: 'Name',
      search: 'Search',
      clearFiltersAria: 'Clear filters',
      clear: 'Clear',
      newButton: '+ New profile',
      filters: { all: 'All', active: 'Active', inactive: 'Inactive' },
      loading: 'Loading profiles…',
      empty: 'No profiles found.',
      editAria: 'Edit profile',
      deleteAria: 'Delete profile',
      toggleActiveAria: 'Active profile — click to deactivate',
      toggleInactiveAria: 'Inactive profile — click to activate',
      toggleActiveTitle: 'Active — click to deactivate',
      toggleInactiveTitle: 'Inactive — click to activate',
      delete: {
        title: 'Delete profile',
        message: 'Are you sure you want to delete profile {{name}}? This action cannot be undone.',
      },
    },
    usersPage: {
      title: 'Users',
      subtitle: 'Manage company users',
      refresh: 'Refresh',
      addButton: 'Add',
      meta: {
        registered: '{{count}} registered',
        page: 'Page {{page}} of {{totalPages}} ({{limit}} per page)',
      },
      refreshing: 'Refreshing…',
      loading: 'Loading users…',
      master: 'Master',
      editAria: 'Edit user',
      deleteAria: 'Delete user',
      empty: {
        none: 'No users registered for this company.',
        page: 'No users on this page.',
      },
      delete: {
        title: 'Delete user',
        message: 'Are you sure you want to delete user {{name}}? This action cannot be undone.',
      },
      errors: {
        notFound: 'Company or user not found.',
        companyNotFound: 'Company not found.',
        deleteDependencies: 'Could not delete user (error removing dependencies).',
        loadFailed: 'Could not load user list.',
        deleteFailed: 'Could not delete user.',
        invalidPagination: 'Invalid pagination parameters.',
        forbiddenList: 'No permission to list users for this company.',
        forbiddenDelete: 'No permission to delete users for this company.',
      },
    },
    userEdit: {
      createTitle: 'New user',
      editTitle: 'Edit user details',
      fields: {
        firstName: 'First name',
        lastName: 'Last name',
        email: 'Email',
        userName: 'Username',
        phone: 'Phone',
        department: 'Department',
        profile: 'Profile',
        language: 'Language',
        timezone: 'Timezone',
        currentProfile: 'Current profile',
      },
      blocked: 'Blocked',
      timezoneGroups: {
        utc: 'UTC offset',
        iana: 'Regional timezone (IANA)',
      },
      resetting: 'Resetting…',
      resetPassword: 'Reset password',
      create: 'Create user',
      passwordDialog: {
        defaultTitle: 'Temporary password',
        resetTitle: 'Password reset',
        createTitle: 'User created',
        hint: 'Share this temporary password with the user. It will not be shown again.',
        copied: 'Password copied to clipboard.',
        copyPassword: 'Copy password',
        copiedButton: 'Copied',
        copy: 'Copy',
        understood: 'Got it',
      },
      errors: {
        phoneInvalid: 'Enter a valid phone number or clear the field.',
        profilesLoadFailed: 'Could not load company profiles.',
        resetFailed: 'Could not reset password.',
        requiredFields: 'Fill in first name, last name, email and username.',
        createFailed: 'Could not create user.',
        updateFailed: 'Could not update user.',
        licenseLimit: 'License user limit reached.',
        duplicate: 'Email or username already exists in this company.',
        notFound: 'Company or user not found.',
      },
    },
    integrations: {
      subtitle: 'SAFR and Lenel',
      safr: {
        loading: 'Loading SAFR configuration…',
        autoSync: 'Automatic sync with SAFR',
        authHint: 'The Authorization field is not filled automatically for security.',
        paramsTitle: 'SAFR parameters',
        authPlaceholder: 'Enter authorization to save',
        saveSuccess: 'SAFR configuration saved successfully.',
        errors: { authRequired: 'Enter authorization (not shown after saving).' },
      },
      lenel: {
        loading: 'Loading Lenel configuration…',
        authInfo: 'Lenel integration authentication information',
        passwordHint: 'Password is not shown after saving. Enter again to change.',
        enabled: 'Enabled',
        saveSuccess: 'Lenel configuration saved.',
        errors: { passwordRequired: 'Enter password (never shown after saving).' },
      },
    },
  },
  operators: {
    title: 'Operator Management',
    subtitle: 'Manage allocated operators, support hours, service notes and timesheets',
    summaryAria: 'Summary',
    stats: {
      activeOperators: 'Active operators',
      allocatedHours: 'Allocated hours',
      usedHours: '{{used}}h used ({{pct}}%)',
      pendingNotes: 'Pending notes',
      pendingApprovals: 'Pending approvals',
    },
    tabs: {
      operators: 'Operators',
      hours: 'Hours',
      notes: 'Notes',
      timesheet: 'Timesheet',
      base: 'Knowledge Base',
      profile: 'My Profile',
    },
    operadores: {
      searchPlaceholder: 'Search by name, email or role...',
      newButton: 'New Operator',
      filters: 'Filters:',
      filterStatusAria: 'Filter by status',
      allStatus: 'All statuses',
      filterClientAria: 'Filter by client',
      allClients: 'All clients',
      count: '{{displayed}} of {{total}} operators',
      retry: 'Try again',
      loading: 'Loading operators…',
      empty: 'No operators to display.',
      table: {
        operator: 'Operator',
        role: 'Role',
        clients: 'Clients',
        hours: 'Hours',
        status: 'Status',
      },
      none: 'None',
      active: 'Active',
      inactive: 'Inactive',
      actionsAria: 'Operator actions',
      edit: 'Edit',
      remove: 'Remove',
      form: {
        editTitle: 'Edit Operator',
        createTitle: 'New Operator',
        subtitle: 'Fill in operator information and linked clients (optional).',
        closeAria: 'Close',
        name: 'Name',
        email: 'Email',
        role: 'Role',
        rolePlaceholder: 'E.g.: Level 2 Support',
        status: 'Status',
        clientsSection: 'Clients (companies)',
        clientsHint: 'Optional. No selection means no clients.',
        noCompanies: 'No companies listed. Check GET /company and permissions.',
        cancel: 'Cancel',
        saving: 'Saving…',
        saveChanges: 'Save changes',
        create: 'Create Operator',
        errorRequired: 'Fill in name, email and role.',
      },
    },
    horas: {
      title: 'Support and Implementation Hours Licensing',
      newPackage: 'New Hours Package',
      retry: 'Try again',
      loading: 'Loading hours packages...',
      empty: 'No hours packages registered.',
      clientsCount: '{{count}} client(s)',
      available: 'Available: {{hours}}h',
      addHours: 'Add hours',
      total: 'Total',
      remaining: '{{hours}}h remaining',
      form: {
        createTitle: 'New Hours Package',
        subtitle: 'Set operator, total hours and companies with their limits.',
        operator: 'Operator',
        select: 'Select...',
        totalHours: 'Total Hours',
        companiesTitle: 'Package Companies',
        companiesHint: 'Add one or more companies with maxHours.',
        addCompany: '+ Company',
        selectCompany: 'Select company...',
        maxHours: 'maxHours',
        cancel: 'Cancel',
        create: 'Create Package',
        saving: 'Saving...',
      },
      addHoursModal: {
        title: 'Add hours',
        hours: 'Hours',
        cancel: 'Cancel',
        confirm: 'Add',
      },
      errors: {
        packageRequired:
          'Select operator, total hours and at least one company with valid allocation.',
        duplicateCompany: 'Do not repeat the same company in the package.',
        hoursExceeded: 'The sum of hours per company cannot exceed the package total.',
        invalidHours: 'Enter a valid number of hours.',
      },
    },
    notas: {
      title: 'Service Notes Issuance',
      newNote: 'New Service Note',
      table: {
        number: 'Number',
        client: 'Client',
        operator: 'Operator',
        hours: 'Hours',
        value: 'Amount',
        date: 'Date',
        status: 'Status',
      },
      status: {
        paid: 'Paid',
        issued: 'Issued',
        pending: 'Pending',
      },
      actionsAria: 'Actions',
    },
    timesheet: {
      title: 'Timesheet',
      newEntry: 'New Entry',
      table: {
        date: 'Date',
        operator: 'Operator',
        client: 'Client',
        hours: 'Hours',
        description: 'Description',
        status: 'Status',
      },
      status: {
        approved: 'Approved',
        pending: 'Pending',
      },
    },
    base: {
      title: 'Knowledge Base',
      subtitle: 'Self-service articles with AI semantic search',
      refreshAria: 'Refresh',
      listViewAria: 'List',
      gridViewAria: 'Grid',
      newArticle: 'New Article',
      searchPlaceholder: 'Search articles...',
      emptyArticles: 'No articles found',
      createFirstArticle: 'Create first article',
      stats: {
        total: 'Total Articles',
        published: 'Published',
        views: 'Views',
        helpful: 'Helpful Ratings',
      },
      subTabs: {
        browse: 'Browse',
        aiSearch: 'AI Search',
      },
    },
    perfil: {
      title: 'My Profile',
      tabs: {
        personal: 'Personal Data',
        preferences: 'Preferences',
      },
      loadError: 'Could not load your data. Refresh the page or try again later.',
      personalInfo: {
        title: 'Personal Information',
        subtitle: 'Update your profile and contact information',
        photoHint: 'Click to change photo (max. 2MB)',
        active: 'Active',
        fullName: 'Full name',
        email: 'Email',
        timezonePlaceholder: 'E.g. UTC+03:00',
        saveError: 'Could not save. Check username and image size (max. 2MB).',
        username: 'Username',
        emailLocked: 'Email cannot be changed',
        phone: 'Phone',
        department: 'Department',
        language: 'Language',
        timezone: 'Timezone',
        saveSuccess: 'Data updated successfully.',
      },
      notifications: {
        title: 'Notifications',
        subtitle: 'Configure how you want to receive alerts',
        system: 'System Notifications',
        systemHint: 'Receive notifications for new tickets',
        email: 'Email Notifications',
        emailHint: 'Receive daily summaries by email',
        sound: 'Sound Alerts',
        soundHint: 'Play sound when receiving new tickets',
      },
      appearance: {
        title: 'Appearance',
        subtitle: 'Customize the system appearance',
        darkMode: 'Dark Mode',
        darkModeHint: 'Switch between light and dark theme',
        compactView: 'Compact View',
        compactViewHint: 'Show more tickets on screen',
        language: 'Language',
        languageHint: 'Select the system language',
      },
      workflow: {
        title: 'Workflow',
        subtitle: 'Configure your default behavior',
        autoAssign: 'Auto-assignment',
        autoAssignHint: 'Automatically assign new tickets from your clients',
      },
      saved: 'Saved',
    },
  },
  param: {
    title: 'Parameterizations',
    subtitle: 'Configure system parameters, modules and analytics.',
    searchPlaceholder: 'Search verticals, modules...',
    tabs: {
      verticals: 'Verticals',
      modules: 'Modules',
      analytics: 'Analytics',
    },
    verticals: {
      title: 'System verticals',
      newButton: '+ New Vertical',
      loading: 'Loading verticals...',
      empty: 'No verticals found.',
      filterHint:
        'Click the cards to filter which verticals appear in the module list below. You can select more than one.',
      selectAll: 'Select all',
      deselectAll: 'Deselect all',
      filterAria: 'Filter vertical {{name}}',
      deactivated: 'Deactivated',
      activeSummary: '{{active}}/{{total}} active',
      selectVerticalHint: 'Select one or more verticals above to see associated modules.',
      expandModulesAria: 'Expand or collapse modules of {{name}}',
      activateAll: 'Activate All',
      deactivateAll: 'Deactivate All',
      editAria: 'Edit vertical',
      deleteAria: 'Delete vertical',
      noModules: 'No modules associated with this vertical.',
      viewMarkdownAria: 'View module markdown',
      form: {
        editTitle: 'Edit Vertical',
        createTitle: 'New Vertical',
        subtitle: 'Enter name, description, color and vertical status.',
        name: 'Vertical Name',
        icon: 'Icon',
        description: 'Description',
        color: 'Vertical color (optional)',
        colorHint: 'If empty, a random color will be generated.',
        status: 'Status',
        active: 'Active',
        inactive: 'Inactive',
        associatedModules: 'Associated Modules ({{count}} selected)',
        noModulesAvailable: 'No modules available for association.',
        cancel: 'Cancel',
        saving: 'Saving...',
        saveChanges: 'Save changes',
        create: 'Create vertical',
        errors: {
          nameRequired: 'Enter vertical name and description.',
          invalidColor: 'Invalid color. Use #RGB or #RRGGBB.',
        },
      },
      delete: {
        title: 'Confirm deletion',
        message: 'Do you really want to remove vertical {{name}}?',
        cancel: 'Cancel',
        confirm: 'Delete',
        deleting: 'Deleting...',
      },
    },
    modules: {
      title: 'System modules',
      expandAll: 'Expand all',
      collapseAll: 'Collapse all',
      newCategory: '+ New Category',
      newModule: '+ New Module',
      loading: 'Loading modules and categories...',
      empty: 'No modules found.',
      expandCategoryAria: 'Expand or collapse category {{name}}',
      editCategoryAria: 'Edit category',
      enableModuleAria: 'Enable module',
      disableModuleAria: 'Disable module',
      viewDetailsAria: 'View module details',
      editAria: 'Edit module',
      deleteAria: 'Delete module',
      noCategory: 'No category',
      view: {
        title: 'Module Details',
        subtitle: 'Full module data view (read-only).',
        loading: 'Loading module data...',
        name: 'Name',
        category: 'Category',
        categoryPathPrefix: 'Route prefix (category)',
        modulePath: 'Route segment (module)',
        activationPath: 'Activation route',
        activationPathMissing:
          'Not defined: category prefix and module route segment are required. The backend composes it automatically when both exist.',
        description: 'Description',
        markdown: 'Training / Documentation (Markdown)',
        attachments: 'Attachments',
        noAttachments: 'No attachments available.',
        close: 'Close',
      },
      create: {
        title: 'New Module',
        subtitle: 'Create a new system module and associate a category (optional).',
        name: 'Module name',
        description: 'Description',
        category: 'Category',
        noCategory: 'No category',
        pathSegment: 'Route segment (module)',
        pathHint: 'A single segment, without "/" or spaces. Optional.',
        pathPlaceholder: 'e.g.: reports',
        sectionName: 'Section name (menu)',
        sectionPlaceholder: 'Default: category name',
        menuIcon: 'Menu icon',
        activationPreview: 'Activation route preview',
        cancel: 'Cancel',
        saving: 'Saving...',
        create: 'Create Module',
        errors: {
          nameRequired: 'Enter the module name.',
        },
      },
      edit: {
        title: 'Edit Module',
        subtitle: 'Update module data and maintain training/documentation material in markdown.',
        loading: 'Loading full module data...',
        pathHint:
          'A single segment, without "/" or spaces. Leave empty to remove the segment (PUT sends empty value as removal).',
        sectionName: 'Section name (menu)',
        markdownTab: {
          write: 'Edit',
          preview: 'Preview',
        },
        attachments: {
          title: 'Attachments',
          uploading: 'Uploading...',
          add: '+ Add file',
          hint: 'Use attachment:attachment-id in markdown, or click "Insert in markdown".',
          empty: 'No attachments added.',
          pendingUpload: ' · pending upload',
          insert: 'Insert in markdown',
          removeAria: 'Remove attachment',
          remote: 'Remote',
          idLabel: 'ID',
        },
        cancel: 'Cancel',
        saving: 'Saving...',
        saveChanges: 'Save changes',
        errors: {
          nameRequired: 'Enter the module name.',
          waitUpload: 'Wait for attachment uploads to finish before saving changes.',
          pendingAttachments:
            'There are attachments pending upload. Re-send the files before saving.',
          saveBeforeInsert:
            'Save the module to complete upload before inserting this attachment in markdown.',
        },
      },
      delete: {
        title: 'Confirm deletion',
        message: 'Do you really want to remove module {{name}}?',
        cancel: 'Cancel',
        confirm: 'Delete',
        deleting: 'Deleting...',
      },
      category: {
        createTitle: 'New Category',
        createSubtitle: 'Create a category to organize modules.',
        editTitle: 'Edit Category',
        editSubtitle:
          'Change the name and route prefix used in the activation URL for modules in this category.',
        name: 'Category name',
        pathPrefix: 'Route prefix (category)',
        pathHint:
          'First segment of the activation URL. A single segment, without "/" or spaces. Optional.',
        pathHintEdit: 'Leave empty to remove the prefix (the backend interprets it as removal).',
        pathPlaceholder: 'e.g.: analytics',
        sectionName: 'Section name (menu)',
        sectionPlaceholder: 'E.g.: Facial',
        svgIcon: 'Icon (SVG)',
        svgFilter: 'Filter by folder or file…',
        svgCatalogHint:
          'Catalog in src/assets/svg. Sent to the API in the svg field as ./assets/media/svg/…',
        cancel: 'Cancel',
        saving: 'Saving...',
        create: 'Create Category',
        save: 'Save',
        errors: {
          nameRequired: 'Enter the category name.',
          invalidIcon: 'Select a valid icon from the list.',
          maxLength: '{{field}} can have at most 128 characters.',
          invalidSegment: '{{field}} must be a single URL segment (no slash or whitespace).',
        },
      },
      activationHint:
        'The activation route (category prefix + module segment) only appears when the selected category has a prefix and the module has a route segment.',
      markdownPlaceholder: 'Describe module training and usage here...',
    },
    analytics: {
      title: 'Analytics catalog',
      newType: '+ New Type',
      newAnalytic: '+ New Analytic',
      loading: 'Loading analytics...',
      empty: 'No analytics found.',
      count: '{{count}} analytics',
      details: 'Details',
      noManufacturers: 'No manufacturers',
      coverage: '{{integrated}}/{{total}}',
      typeForm: {
        title: 'New Analytic Type',
        subtitle: 'Register a category to group analytics.',
        name: 'Name',
        code: 'Code',
        description: 'Description',
        cancel: 'Cancel',
        saving: 'Saving...',
        save: 'Save',
        errors: {
          required: 'Enter the analytic type name and code.',
        },
      },
      analyticForm: {
        title: 'New Analytic',
        subtitle: 'Fill in the details to add a new analytic to the catalog.',
        code: 'Code',
        name: 'Name',
        category: 'Category',
        icon: 'Icon',
        shortDescription: 'Short Description',
        fullDescription: 'Full Details',
        useCases: 'Use Cases',
        compatibleManufacturers: 'Compatible Manufacturers',
        select: 'Select',
        cancel: 'Cancel',
        saving: 'Saving...',
        save: 'Save',
        errors: {
          required: 'Fill in code, name and category.',
        },
      },
      detailsPanel: {
        title: 'Analytic Details',
        description: 'Description',
        noDescription: 'No description registered.',
        useCases: 'Use cases',
        manufacturerCompatibility: 'Manufacturer Compatibility',
        yes: 'YES',
        no: 'NO',
      },
    },
    iconPicker: {
      noIcon: 'No icon',
    },
  },
  tickets: {
    title: 'Tickets',
    subtitle: 'Support ticket hub for every licensed client.',
    summaryAria: 'Ticket summary',
    operator: {
      title: 'Logged-in Operator',
      loading: 'Loading...',
      onlyMine: 'My tickets ({{count}})',
      all: 'All operators',
      hint: 'Select a company in the filter to list every operator registered under it.',
    },
    stats: {
      total: 'Total Tickets',
      open: 'Open',
      closed: 'Closed',
      highPriority: 'High Priority',
      unansweredToday: 'Unanswered today',
    },
    severity: {
      low: 'Low',
      medium: 'Medium',
      high: 'High',
    },
    priority: {
      low: 'Low',
      medium: 'Medium',
      high: 'High',
      urgent: 'Urgent',
    },
    export: 'Export',
    exportFilename: 'tickets',
    refresh: 'Refresh',
    view: {
      compact: 'Compact view',
      list: 'List view',
      grid: 'Grid view',
    },
    searchPlaceholder: 'Search by title, description, location or company...',
    filters: {
      search: 'Search',
      company: 'Company',
      status: 'Status',
      type: 'Type',
      severity: 'Priority',
      from: 'From date',
      to: 'To date',
      clear: 'Clear filters',
      allCompanies: 'All companies',
      allStatuses: 'All statuses',
      allTypes: 'All types',
      allSeverities: 'All priorities',
    },
    tabs: {
      all: 'All',
      open: 'Open',
      closed: 'Closed',
    },
    newButton: 'New Ticket',
    loading: 'Loading tickets...',
    empty: 'No tickets found.',
    table: {
      title: 'Ticket',
      company: 'Company',
      location: 'Location',
      type: 'Type',
      severity: 'Priority',
      status: 'Status',
      createdAt: 'Created at',
      updatedAt: 'Updated at',
    },
    detail: {
      close: 'Close',
      status: 'Status:',
      loadingConversation: 'Loading conversation...',
      noMessages: 'No messages yet.',
      replyPlaceholder: 'Write a reply...',
      send: 'Send',
      sending: 'Sending...',
      attachment: 'Attachment',
      slaDueAt: 'SLA due',
      assignee: 'Assignee',
      internalNote: 'Internal note',
      markInternal: 'Mark as internal note (not client-visible yet)',
      linkSolution: 'Link a solution (optional)',
      linkSolutionHint: 'Helps measure the effectiveness of solutions in the Knowledge Base.',
      noSolution: 'No solution linked',
    },
    form: {
      title: 'New Ticket',
      subtitle: 'Open a support ticket on behalf of a licensed client.',
      sections: {
        company: 'Company Information',
        details: 'Ticket Details',
      },
      company: 'Company',
      selectCompany: 'Select the company',
      location: 'Location',
      selectLocation: 'Select the location',
      loadingLocations: 'Loading locations...',
      type: 'Type',
      status: 'Status',
      severity: 'Priority',
      priority: 'Priority',
      assignee: 'Assignee (Fisital team)',
      unassigned: 'Unassigned',
      titleField: 'Subject',
      titlePlaceholder: 'Briefly describe the issue',
      description: 'Description',
      descriptionPlaceholder: 'Additional details about the ticket...',
      attachments: 'Evidence (screenshots)',
      cancel: 'Cancel',
      saving: 'Saving...',
      create: 'Create ticket',
      errors: {
        required: 'Fill in company, location, type, status, title and description.',
        attachmentsFailed: 'Ticket created, but the attachment upload failed: {{message}}',
      },
    },
  },
  knowledgeBase: {
    title: 'Knowledge Base',
    subtitle: 'Problem/solution catalog fed by the ticket history.',
    searchPlaceholder: 'Search by name, module or product...',
    newCategory: 'New category',
    empty: 'No problem category registered yet.',
    occurrences: 'Occurrences',
    solutions: 'Solutions',
    noSolutions: 'No solution registered for this category yet.',
    newSolution: 'New solution',
    addSolution: 'Add solution',
    removeCategory: 'Remove category',
    activate: 'Reactivate',
    deactivate: 'Deactivate',
    loadError: 'Could not load the knowledge base.',
    confirmRemoveCategory: 'Remove the category "{{name}}"? Its solutions go with it.',
    confirmRemoveSolution: 'Remove the solution "{{title}}"?',
    severity: {
      low: 'Low',
      medium: 'Medium',
      high: 'High',
    },
    form: {
      name: 'Problem name',
      module: 'Module',
      product: 'Product',
      description: 'Description',
      solutionTitle: 'Solution title',
      tags: 'Tags (comma-separated)',
      steps: 'Steps (one per line)',
    },
    table: {
      title: 'Solution',
      usage: 'Uses',
      effectiveness: 'Effectiveness',
      avgTime: 'Avg. time',
    },
  },
};
